/** RoleRoute — server-verified RBAC at the route level (plan Phase 7.3).
 *
 * GroupedNav's localStorage filtering is cosmetic; this wrapper re-checks
 * the actor role against /auth/me (server truth) and renders a 403 page
 * when a staff role deep-links into an admin-only page. Admin tokens
 * (no role claim → "admin") see everything. When /auth/me is unavailable
 * (legacy backend, 404) or the network fails, we allow — same philosophy
 * as DashboardLayout's B2 fallback, which keeps legacy deployments working.
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Block as BlockIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { authApi } from "../../api/client";
import { NAV_GROUPS } from "../navigation/GroupedNav";

/** path → roles allowed (only items with an explicit roles array). */
const PATH_ROLES: Record<string, string[]> = {};
for (const group of NAV_GROUPS) {
  for (const item of group.items) {
    if (item.roles && item.roles.length > 0) {
      PATH_ROLES[item.path] = item.roles as string[];
    }
  }
}

function rolesForPath(pathname: string): string[] | null {
  if (PATH_ROLES[pathname]) return PATH_ROLES[pathname];
  // Longest-prefix match so detail pages (e.g. /servers/:id) inherit the
  // section contract. No match at all → admin-only (safe default).
  let best = "";
  for (const p of Object.keys(PATH_ROLES)) {
    if (pathname.startsWith(p + "/") && p.length > best.length) best = p;
  }
  return best ? PATH_ROLES[best] : null;
}

function isAllowed(role: string, pathname: string): boolean {
  const normalized = (role || "admin").toLowerCase();
  if (normalized === "admin") return true;
  const allowed = rolesForPath(pathname);
  if (allowed === null) return false; // admin-only page
  return allowed.includes(normalized);
}

function Forbidden({ role }: { role: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        gap: 2,
      }}
    >
      <BlockIcon color="error" sx={{ fontSize: 64 }} />
      <Typography variant="h4">403 — Access denied</Typography>
      <Alert severity="warning" sx={{ maxWidth: 480 }}>
        Your role ({role}) does not have access to this page. Ask an
        administrator if you believe this is a mistake.
      </Alert>
      <Button
        variant="contained"
        startIcon={<ArrowBackIcon />}
        onClick={() => {
          window.history.back();
        }}
      >
        Go back
      </Button>
    </Box>
  );
}

type CheckState =
  | { kind: "checking" }
  | { kind: "allowed" }
  | { kind: "forbidden"; role: string };

export default function RoleRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<CheckState>({ kind: "checking" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "checking" });
    authApi
      .me()
      .then((res) => {
        if (cancelled) return;
        if (!res.data?.authenticated) {
          navigate("/login?expired=1", { replace: true });
          return;
        }
        const role = String(res.data?.role || "admin");
        if (isAllowed(role, location.pathname)) {
          setState({ kind: "allowed" });
        } else {
          setState({ kind: "forbidden", role });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          navigate("/login?expired=1", { replace: true });
        } else {
          // 404 (legacy backend without /auth/me) or transient network
          // error — allow through, matching the layout's fallback.
          setState({ kind: "allowed" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate]);

  if (state.kind === "checking") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (state.kind === "forbidden") {
    return <Forbidden role={state.role} />;
  }
  return <>{children}</>;
}
